import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Award, Target, Heart, Shield, Users, TrendingUp } from 'lucide-react';

export default function About() {
  const values = [
    {
      icon: Award,
      title: 'Quality & Compliance',
      description: 'We adhere to the highest global standards in pharmaceutical manufacturing, ensuring every product meets rigorous quality control requirements.',
    },
    {
      icon: Target,
      title: 'Scientific Integrity',
      description: 'Evidence-based research and rigorous clinical validation drive all our development programs and decision-making processes.',
    },
    {
      icon: Heart,
      title: 'Patient First',
      description: 'Every decision we make prioritizes patient safety, efficacy, and access to essential medicines that improve quality of life.',
    },
    {
      icon: Shield,
      title: 'Ethical Business Practices',
      description: 'Transparency, accountability, and ethical conduct form the foundation of our operations and relationships with stakeholders.',
    },
    {
      icon: TrendingUp,
      title: 'Sustainable Growth',
      description: 'Our commitment to sustainable practices ensures long-term value for patients, partners, and communities we serve.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-10 lg:py-10">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
              About Us
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground font-['Space_Grotesk'] leading-tight">
              Excellence in Healthcare
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Apken Pharma is built on scientific excellence, robust quality systems, and patient-centric values.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-10">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Card className="border-border shadow-lg">
              <CardContent className="p-8 lg:p-12">
                <h2 className="text-3xl font-bold text-foreground mb-6 font-['Space_Grotesk']">Our Mission</h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                  At Apken Pharma, we are committed to delivering high-quality, affordable medicines that improve patient outcomes globally. Through innovation, compliance, and ethical practices, we address unmet medical needs across multiple therapeutic areas.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Commercialize pharmaceutical products that meet stringent global regulatory standards, ensuring that patients worldwide have access to safe and effective treatments.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-10 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <Badge className="bg-secondary/10 text-secondary hover:bg-secondary/20 border-secondary/20">
              Our Values
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground font-['Space_Grotesk']">
              Principles That Guide Us
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our core values shape every aspect of our operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="card-hover border-border">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-10">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-accent/10 text-accent hover:bg-accent/20 border-accent/20">
                Our Commitment
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground font-['Space_Grotesk']">
                Advancing Global Healthcare
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                We are dedicated to making a meaningful difference in the lives of patients worldwide through our unwavering commitment to quality, innovation, and accessibility.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}